import React from 'react'
import { Link } from '@inertiajs/react'
import { CalendarOff, Coins } from 'lucide-react'
import type { NotPricedStatus } from '@/types/shared'

interface NotPricedCalendarStateProps {
  /** Backend-derived status of the selected room: only a status under which nothing is priced. */
  status: NotPricedStatus
  roomName: string
  /** The property's backend-served canonical path. */
  propertyBasePath: string
}

/**
 * Explanation state for the Prices calendar when the selected room type is not
 * being priced by the engine. Replaces the month grid (a wall of empty cells
 * explains nothing) with the design-system empty-state pattern. Presentation of
 * absence only — the status itself comes from RoomType#pricing_status.
 *
 * The calendar's rows are the building's rooms, so a Same room (combined inventory), whose own
 * statuses name its twin, is never the selected room here.
 */
export default function NotPricedCalendarState({
  status,
  roomName,
  propertyBasePath,
}: NotPricedCalendarStateProps) {
  const isNoPlans = status === 'no_active_plans'
  const Icon = isNoPlans ? CalendarOff : Coins

  return (
    <div className="rounded-xl border border-dashed border-border bg-white px-6 py-16 text-center shadow-sm">
      <Icon className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <p className="mt-3 text-sm font-medium text-foreground">
        {isNoPlans ? `${roomName} isn't priced` : 'No base rate set'}
      </p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
        {isNoPlans
          ? 'This room has no active rate plans in your PMS, so there is nothing for the rate engine to price. Manage its plans there.'
          : `${roomName} needs a base rate before the rate engine can price it. Set one in Rooms Setup and this calendar will fill in after the next computation.`}
      </p>
      {!isNoPlans && (
        <Link
          href={`${propertyBasePath}/pricing-rules/rooms-setup`}
          className="mt-5 inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
        >
          Set base rate
        </Link>
      )}
    </div>
  )
}
